// client/src/components/StatisticsPage.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  FaArrowLeft, FaChartLine, FaUsers, FaStar, FaFutbol, 
  FaFire, FaShieldAlt, FaSkull, FaHeartbeat, FaCalendarAlt, FaInfoCircle,
  FaFilter, FaGlobe, FaTrophy, FaMedal
} from 'react-icons/fa';
import api from '../services/api';

// ========== Stat Card ==========
const StatCard = ({ title, value, icon, color }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`bg-gradient-to-br ${color} rounded-2xl p-4 shadow-xl border border-white/10 backdrop-blur-sm`}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-300 text-xs uppercase tracking-wide">{title}</p>
        <p className="text-3xl md:text-4xl font-bold text-white mt-1">{value}</p>
      </div>
      <div className="text-4xl opacity-80">{icon}</div>
    </div>
  </motion.div>
);

// ========== Player Avatar ==========
const PlayerAvatar = ({ position, rarity, size = 56 }) => {
  const pixelSize = `${size}px`;
  const getImageSrc = () => {
    const positionMap = {
      Striker: 'striker',
      Defender: 'defender',
      Midfielder: 'midfielder',
      Goalkeeper: 'goalkeeper'
    };
    const baseName = positionMap[position] || 'player';
    if (rarity === 'gold' || rarity === 'silver' || rarity === 'bronze') {
      return `/img/${rarity}_${baseName}.jpg`;
    }
    return `/img/${baseName}.jpg`;
  };
  
  return (
    <div 
      className="rounded-full overflow-hidden shadow-lg ring-2 ring-white/30 bg-gray-900 flex-shrink-0"
      style={{ width: pixelSize, height: pixelSize }}
    >
      <img
        src={getImageSrc()}
        alt={position}
        className="w-full h-full object-cover"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src = '/img/player.jpg';
        }}
      />
    </div>
  );
};

// ========== Player Stats Table (Desktop) ==========
const PlayerStatsTable = ({ stats, t }) => {
  if (!stats.length) {
    return (
      <div className="text-center py-12 text-gray-400">
        <FaFutbol className="text-6xl mx-auto mb-3 opacity-30" />
        <p className="text-lg">{t('statistics.noMatches')}</p>
        <p className="text-sm">{t('statistics.playFirstMatch')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto hidden md:block">
      <table className="w-full text-sm">
        <thead className="bg-black/40 border-b border-yellow-500/20">
          <tr className="text-left text-gray-300">
            <th className="px-4 py-3 font-semibold">{t('statistics.player')}</th>
            <th className="px-4 py-3 font-semibold text-center">{t('statistics.apps')}</th>
            <th className="px-4 py-3 font-semibold text-center">⚽ {t('statistics.goals')}</th>
            <th className="px-4 py-3 font-semibold text-center">🟨 {t('statistics.yellow')}</th>
            <th className="px-4 py-3 font-semibold text-center">🟥 {t('statistics.red')}</th>
            <th className="px-4 py-3 font-semibold text-center">🩹 {t('statistics.injury')}</th>
            <th className="px-4 py-3 font-semibold text-center">📊 {t('statistics.mins')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {stats.map((player, idx) => (
            <motion.tr 
              key={player.user_card_id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.03 }}
              className="hover:bg-white/5 transition"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-4">
                  <PlayerAvatar 
                    position={player.position} 
                    rarity={player.rarity} 
                    size={56}
                  />
                  <div>
                    <div className="font-semibold text-white text-base">{player.player_name}</div>
                    <div className="text-xs text-gray-400">{player.position}</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-center font-mono text-gray-300">{player.appearances}</td>
              <td className="px-4 py-3 text-center text-yellow-400 font-bold text-lg">{player.total_goals}</td>
              <td className="px-4 py-3 text-center text-yellow-600">{player.total_yellow_cards}</td>
              <td className="px-4 py-3 text-center text-red-500 font-semibold">{player.total_red_cards}</td>
              <td className="px-4 py-3 text-center text-rose-400">{player.total_injuries}</td>
              <td className="px-4 py-3 text-center text-gray-400">{player.total_minutes}</td>
            </motion.tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ========== Player Stats Cards (Mobile) ==========
const PlayerStatsCards = ({ stats, t }) => {
  if (!stats.length) {
    return (
      <div className="text-center py-12 text-gray-400 md:hidden">
        <FaFutbol className="text-6xl mx-auto mb-3 opacity-30" />
        <p className="text-lg">{t('statistics.noMatches')}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
      {stats.map((player) => (
        <div key={player.user_card_id} className="bg-black/40 rounded-xl p-4 border border-white/10">
          <div className="flex items-center gap-4 mb-3">
            <PlayerAvatar 
              position={player.position} 
              rarity={player.rarity} 
              size={64}
            />
            <div>
              <div className="font-bold text-white text-lg">{player.player_name}</div>
              <div className="text-xs text-gray-400">{player.position}</div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div className="bg-black/30 rounded-lg p-2">
              <div className="text-gray-400 text-xs">{t('statistics.apps')}</div>
              <div className="text-white font-bold">{player.appearances}</div>
            </div>
            <div className="bg-black/30 rounded-lg p-2">
              <div className="text-gray-400 text-xs">⚽</div>
              <div className="text-yellow-400 font-bold">{player.total_goals}</div>
            </div>
            <div className="bg-black/30 rounded-lg p-2">
              <div className="text-gray-400 text-xs">🟨</div>
              <div className="text-yellow-600">{player.total_yellow_cards}</div>
            </div>
            <div className="bg-black/30 rounded-lg p-2">
              <div className="text-gray-400 text-xs">🟥</div>
              <div className="text-red-500">{player.total_red_cards}</div>
            </div>
            <div className="bg-black/30 rounded-lg p-2">
              <div className="text-gray-400 text-xs">🩹</div>
              <div className="text-rose-400">{player.total_injuries}</div>
            </div>
            <div className="bg-black/30 rounded-lg p-2">
              <div className="text-gray-400 text-xs">{t('statistics.mins')}</div>
              <div className="text-gray-300">{player.total_minutes}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ========== Card Evolution ==========
const CardEvolutionTable = ({ cards, t }) => {
  const [expandedCard, setExpandedCard] = useState(null);

  if (!cards.length) {
    return (
      <div className="text-center py-12 text-gray-400">
        <FaStar className="text-6xl mx-auto mb-3 opacity-30" />
        <p className="text-lg">{t('statistics.noCardEvolution')}</p>
        <p className="text-sm">{t('statistics.playMatchesToEvolve')}</p>
      </div>
    );
  }

  const getRarityBorder = (rarity) => {
    switch(rarity) {
      case 'gold': return 'border-yellow-500/50';
      case 'silver': return 'border-gray-400/50';
      case 'bronze': return 'border-amber-600/50';
      default: return 'border-blue-500/30';
    }
  };

  const AttributeBadge = ({ label, value }) => (
    <div className="flex flex-col items-center bg-black/40 rounded-xl p-2 min-w-[70px] border border-white/10">
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
      <span className="font-mono text-xl font-bold text-white">{Math.round(value)}</span>
    </div>
  );

  return (
    <div className="space-y-3">
      {cards.map((card) => (
        <div 
          key={card.card_id} 
          className={`bg-black/30 rounded-xl border ${getRarityBorder(card.rarity)} overflow-hidden`}
        >
          <button
            onClick={() => setExpandedCard(expandedCard === card.card_id ? null : card.card_id)}
            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5 transition text-left"
          >
            <div className="flex items-center gap-4">
              <PlayerAvatar 
                position={card.position} 
                rarity={card.rarity} 
                size={56}
              />
              <div>
                <div className="font-bold text-white text-md">{card.name}</div>
                <div className="text-xs text-gray-300 flex flex-wrap items-center gap-1">
                  <span>{card.position}</span>
                  <span>•</span>
                  <span className="capitalize">{card.rarity}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 hidden sm:inline">{card.match_stats.length} {t('statistics.matches')}</span>
              <motion.div
                animate={{ rotate: expandedCard === card.card_id ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="text-yellow-400"
              >
                <FaInfoCircle />
              </motion.div>
            </div>
          </button>

          <AnimatePresence>
            {expandedCard === card.card_id && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="p-4 border-t border-white/10 bg-black/30">
                  <div className="mb-5">
                    <h4 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                      <FaChartLine /> {t('statistics.currentAttributes')}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      <AttributeBadge label={t('statistics.attack')} value={card.current_stats.attack} />
                      <AttributeBadge label={t('statistics.defense')} value={card.current_stats.defense} />
                      <AttributeBadge label={t('statistics.speed')} value={card.current_stats.speed} />
                      <AttributeBadge label={t('statistics.accuracy')} value={card.current_stats.accuracy} />
                    </div>
                  </div>

                  {card.match_stats && card.match_stats.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
                        <FaCalendarAlt /> {t('statistics.matchHistory')}
                      </h4>
                      <div className="max-h-48 overflow-y-auto space-y-2 pr-1 custom-scroll">
                        {card.match_stats.map((match, idx) => (
                          <div key={idx} className="bg-black/40 rounded-lg p-2 text-sm border border-white/5">
                            <div className="flex justify-between items-center flex-wrap gap-1">
                              <span className="text-gray-400 text-xs">{match.match_date}</span>
                              <div className="flex gap-2 text-xs">
                                {match.goals > 0 && <span className="text-yellow-400">⚽ {match.goals}</span>}
                                {match.yellow_cards > 0 && <span className="text-yellow-600">🟨 {match.yellow_cards}</span>}
                                {match.red_cards > 0 && <span className="text-red-500">🟥 {match.red_cards}</span>}
                                {match.injuries > 0 && <span className="text-rose-400">🩹 {match.injuries}</span>}
                              </div>
                            </div>
                            <div className="text-gray-500 text-xs">{t('statistics.playedMinutes', { minutes: match.minutes_played })}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

// ========== Season Selector ==========
const SeasonSelector = ({ seasons, selectedSeason, onSeasonChange, t }) => {
  if (!seasons || seasons.length === 0) {
    return (
      <div className="flex items-center gap-2 text-gray-400 text-sm">
        <FaCalendarAlt className="text-yellow-400" />
        <span>{t('statistics.noSeasonsAvailable')}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 text-gray-300 text-sm">
        <FaFilter className="text-yellow-400" />
        <span className="font-medium">{t('statistics.season')}:</span>
      </div>
      <select
        value={selectedSeason || ''}
        onChange={(e) => onSeasonChange(e.target.value ? parseInt(e.target.value) : null)}
        className="bg-gray-800/80 border border-gray-600 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
      >
        <option value="">{t('statistics.allSeasons')}</option>
        {seasons.map((season) => (
          <option key={season} value={season}>
            {t('statistics.seasonNumber', { number: season })}
          </option>
        ))}
      </select>
    </div>
  );
};

// ========== Competition Filter ==========
const CompetitionFilter = ({ selectedCompetition, onCompetitionChange, t }) => {
  const competitions = [
    { id: 'all', label: t('statistics.allCompetitions'), icon: <FaGlobe className="text-gray-400" /> },
    { id: 'domestic', label: t('competition.domestic'), icon: <FaGlobe className="text-green-400" /> },
    { id: 'champions', label: t('competition.champions'), icon: <FaTrophy className="text-yellow-400" /> },
    { id: 'europa', label: t('competition.europa'), icon: <FaMedal className="text-blue-400" /> },
  ];

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-gray-400 text-sm font-medium mr-1">{t('statistics.competition')}:</span>
      {competitions.map((comp) => (
        <button
          key={comp.id}
          onClick={() => onCompetitionChange(comp.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            selectedCompetition === comp.id
              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-400/50'
              : 'bg-gray-700/30 text-gray-300 hover:bg-gray-700/50 border border-transparent'
          }`}
        >
          {comp.icon}
          <span>{comp.label}</span>
        </button>
      ))}
    </div>
  );
};

// ========== Main Component ==========
const StatisticsPage = ({ userId, onBack }) => {
  const { t, i18n } = useTranslation();
  const [playerStats, setPlayerStats] = useState([]);
  const [cardEvolution, setCardEvolution] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedCompetition, setSelectedCompetition] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('players');

  // جلب قائمة المواسم المتاحة للمستخدم
  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        const res = await api.get(`/statistics/seasons/${userId}`);
        setSeasons(res.data);
        if (res.data.length > 0) {
          setSelectedSeason(res.data[res.data.length - 1]);
        }
      } catch (err) {
        console.error('Failed to fetch seasons:', err);
      }
    };
    if (userId) fetchSeasons();
  }, [userId]);

  // جلب الإحصائيات عند تغيير الموسم أو المسابقة أو المستخدم
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        let url = `/statistics/player-stats/${userId}`;
        const params = new URLSearchParams();
        if (selectedSeason) params.append('season', selectedSeason);
        if (selectedCompetition && selectedCompetition !== 'all') params.append('competition', selectedCompetition);
        if (params.toString()) url += `?${params.toString()}`;

        console.log(`📊 Fetching player stats with URL: ${url}`);
        const playerRes = await api.get(url);
        if (playerRes.data.success) {
          setPlayerStats(playerRes.data.statistics);
        } else {
          setPlayerStats([]);
        }

        let cardUrl = `/statistics/card-evolution/${userId}`;
        if (params.toString()) cardUrl += `?${params.toString()}`;

        console.log(`📊 Fetching card evolution with URL: ${cardUrl}`);
        const cardRes = await api.get(cardUrl);
        if (cardRes.data.success) {
          setCardEvolution(cardRes.data.cards);
        } else {
          setCardEvolution([]);
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
        setPlayerStats([]);
        setCardEvolution([]);
      } finally {
        setLoading(false);
      }
    };
    if (userId) fetchStats();
  }, [userId, selectedSeason, selectedCompetition]);

  const totalMatches = playerStats.length > 0 ? playerStats[0].appearances : 0;
  const totalGoals = playerStats.reduce((sum, p) => sum + (p.total_goals || 0), 0);
  const totalYellow = playerStats.reduce((sum, p) => sum + (p.total_yellow_cards || 0), 0);
  const totalRed = playerStats.reduce((sum, p) => sum + (p.total_red_cards || 0), 0);
  const totalInjuries = playerStats.reduce((sum, p) => sum + (p.total_injuries || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-900 to-emerald-950 flex items-center justify-center">
        <div className="text-yellow-400 text-xl animate-pulse flex items-center gap-3">
          <FaChartLine className="animate-spin" /> {t('statistics.loading')}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-green-900 to-emerald-950 text-white" dir={i18n.dir()}>
      <div className="bg-black/40 backdrop-blur-md border-b border-yellow-500/30 sticky top-0 z-10 px-4 md:px-6 py-4 flex justify-between items-center">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-gray-300 hover:text-yellow-400 transition group"
        >
          <FaArrowLeft className="group-hover:-translate-x-1 transition" />
          <span className="hidden sm:inline">{t('common.back')}</span>
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600 flex items-center gap-2">
          <FaChartLine /> {t('statistics.title')}
        </h1>
        <div className="w-12 md:w-20"></div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <SeasonSelector
            seasons={seasons}
            selectedSeason={selectedSeason}
            onSeasonChange={setSelectedSeason}
            t={t}
          />
          <CompetitionFilter
            selectedCompetition={selectedCompetition}
            onCompetitionChange={setSelectedCompetition}
            t={t}
          />
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-8">
          <StatCard title={t('statistics.matchesPlayed')} value={totalMatches} icon={<FaFutbol />} color="from-blue-900/40 to-blue-800/20" />
          <StatCard title={t('statistics.goals')} value={totalGoals} icon={<FaFire className="text-yellow-400" />} color="from-yellow-900/40 to-yellow-800/20" />
          <StatCard title={t('statistics.yellowCards')} value={totalYellow} icon={<FaShieldAlt />} color="from-yellow-700/40 to-yellow-600/20" />
          <StatCard title={t('statistics.redCards')} value={totalRed} icon={<FaSkull className="text-red-400" />} color="from-red-900/40 to-red-800/20" />
          <StatCard title={t('statistics.injuries')} value={totalInjuries} icon={<FaHeartbeat className="text-rose-400" />} color="from-purple-900/40 to-purple-800/20" />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 md:gap-2 mb-6 border-b border-white/10 pb-2">
          <button
            onClick={() => setActiveTab('players')}
            className={`px-4 md:px-5 py-2 rounded-t-lg font-medium transition flex items-center gap-2 text-sm md:text-base ${
              activeTab === 'players' 
                ? 'bg-yellow-500/20 text-yellow-400 border-b-2 border-yellow-400' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <FaUsers /> <span className="hidden sm:inline">{t('statistics.playerStats')}</span>
          </button>
          <button
            onClick={() => setActiveTab('cards')}
            className={`px-4 md:px-5 py-2 rounded-t-lg font-medium transition flex items-center gap-2 text-sm md:text-base ${
              activeTab === 'cards' 
                ? 'bg-yellow-500/20 text-yellow-400 border-b-2 border-yellow-400' 
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <FaStar /> {t('statistics.cardEvolution')}
          </button>
        </div>

        {/* Content */}
        <div className="bg-black/30 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden p-4">
          {activeTab === 'players' && (
            <>
              <PlayerStatsTable stats={playerStats} t={t} />
              <PlayerStatsCards stats={playerStats} t={t} />
            </>
          )}
          {activeTab === 'cards' && <CardEvolutionTable cards={cardEvolution} t={t} />}
        </div>

        <p className="text-center text-gray-500 text-xs mt-6">
          {t('statistics.footer')}
        </p>
      </div>

      <style jsx>{`
        .custom-scroll::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.05);
          border-radius: 10px;
        }
        .custom-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,255,0,0.3);
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default StatisticsPage;